import React from 'react';
import { FileText, Shield, AlertCircle, Mail, Phone } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEOHelmet from '../components/SEOHelmet';
import Breadcrumbs from '../components/Breadcrumbs';

const EulaPage = () => {
  return (
    <div>
      <SEOHelmet 
        title="End-User License Agreement | Legal Document Specialist"
        description="End-user license agreement for Legal Document Specialist's Payment Applicator tool. Terms and conditions for authorized staff use."
        keywords="EULA, license agreement, terms of use, legal document services"
        canonical="/eula"
      />
      <Header />
      <Breadcrumbs />

      {/* Hero Section */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '160px 0 80px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto', textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              backgroundColor: 'var(--brand-primary)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 2rem',
              color: 'white'
            }}>
              <FileText size={40} />
            </div>
            
            <h1 className="brand-display" style={{
              marginBottom: '1.5rem',
              color: 'var(--text-primary)',
              fontSize: 'clamp(2rem, 4vw, 3rem)'
            }}>
              End-User License Agreement
            </h1>
            
            <p className="body-large" style={{
              color: 'var(--text-secondary)',
              marginBottom: '2rem',
              lineHeight: '1.8'
            }}>
              Legal Document Specialist Payment Applicator Terms
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section style={{ backgroundColor: 'var(--bg-card)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            
            {/* License Grant */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EFF6FF',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0
                }}>
                  <Shield size={24} color="var(--brand-primary)" />
                </div>
                <div style={{ flex: 1 }}>
                  <h2 className="heading-2" style={{ marginBottom: '1rem' }}>
                    License Grant
                  </h2>
                  <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                    Legal Document Specialist, Inc. ("LDS") licenses the Payment Applicator to authorized LDS staff only.
                  </p>
                </div>
              </div>
            </div>

            {/* Tool Description */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                About the Payment Applicator
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '1rem' }}>
                The app matches invoice numbers on a check stub to invoices in LDS's QuickBooks Online company. It does not post a payment until a person reviews the preview and approves it. It does not give legal advice.
              </p>
            </div>

            {/* Usage Restrictions */}
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '2rem',
              marginBottom: '2rem'
            }}>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <AlertCircle size={28} color="#F59E0B" style={{ flexShrink: 0 }} />
                <div>
                  <h3 className="heading-3" style={{ marginBottom: '1rem', color: '#92400E' }}>
                    Usage Restrictions
                  </h3>
                  <ul style={{ 
                    paddingLeft: '1.5rem',
                    color: '#78350F',
                    lineHeight: '1.8'
                  }}>
                    <li style={{ marginBottom: '0.75rem' }}>
                      You may use it only for LDS bookkeeping.
                    </li>
                    <li style={{ marginBottom: '0.75rem' }}>
                      Do not share Intuit keys, tokens, or login.
                    </li>
                    <li style={{ marginBottom: '0.75rem' }}>
                      Do not connect a QuickBooks company you are not authorized to use.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* As-Is Provision */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)',
              marginBottom: '2rem'
            }}>
              <h3 className="heading-3" style={{ marginBottom: '1.5rem' }}>
                As-Is Provision
              </h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8' }}>
                LDS provides the tool as-is for internal use. Intuit/QuickBooks remain subject to Intuit's own terms.
              </p>
            </div>

            {/* Contact Information */}
            <div style={{
              backgroundColor: 'var(--bg-page)',
              padding: '2.5rem',
              borderRadius: '12px',
              border: '1px solid var(--border-medium)'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  backgroundColor: '#EFF6FF',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Mail size={24} color="var(--brand-primary)" />
                </div>
                <h3 className="heading-3">Questions</h3>
              </div>
              
              <p className="body-medium" style={{ color: 'var(--text-secondary)', lineHeight: '1.8', marginBottom: '1.5rem' }}>
                support@legaldocspecialist.com or (760) 866-9182. 225 South Civic Drive, Suite 2-16, Palm Springs, CA 92262.
              </p>

              <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <a 
                  href="mailto:support@legaldocspecialist.com"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'var(--brand-primary)',
                    color: 'white',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  <Mail size={18} />
                  Email Us
                </a>
                <a href="/quote"
                  style={{
                    padding: '0.75rem 1.5rem',
                    backgroundColor: 'transparent',
                    color: 'var(--brand-primary)',
                    border: '2px solid var(--brand-primary)',
                    borderRadius: '6px',
                    textDecoration: 'none',
                    fontWeight: '600',
                    fontSize: '0.9rem',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.5rem'
                  }}
                >
                  Request a Quote
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section style={{ backgroundColor: 'var(--bg-page)', padding: '60px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <div style={{
              backgroundColor: '#FEF3C7',
              border: '2px solid #F59E0B',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center'
            }}>
              <p className="body-small" style={{ color: '#78350F', fontStyle: 'italic' }}>
                This is an operational license for staff, not legal advice. Last updated August 23, 2026.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default EulaPage;
